/**
 * ResourcesScreen — PowerUp CPU/NET and buy/sell RAM
 * Порт логики из webdex PowerUp.tsx, адаптированный для нашего стека
 */

import { useEffect, useState } from 'react'
import type { Session } from '@wharfkit/session'
import {
  powerUp,
  buyRam,
  sellRam,
  estimatePowerUpFeeFromFracs,
  accountExists,
  fetchRamPrice,
  ramBytesForEos,
  ramEosForBytes,
  getExplorerUrl,
  type RamPrice,
} from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'
import BottomNav from '../components/BottomNav'

function fmtBytes(b: number): string {
  if (b < 1024) return `${b}B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(2)}KB`
  return `${(b / (1024 * 1024)).toFixed(2)}MB`
}

const RAM_PRESETS_KB = [1, 4, 8, 16]
// Presets in EOS, not just KB — buying "16 EOS worth of RAM" is a normal way
// to think about it (a deploy budget, a round number to send), and KB-only
// presets left no fast path for that.
const RAM_PRESETS_EOS = [1, 5, 10, 16, 20]

const CPU_PRESETS = [10, 100, 500, 10_000]
const NET_PRESETS = [10, 100, 500, 10_000]
// Buffer applied on top of the live on-chain fee estimate to absorb price
// movement between "estimate" and "broadcast" (powerup market price is dynamic).
const MAX_PAYMENT_BUFFER = 1.3
const CPU_FULL_FRAC = 1_000_000_000_000_000n

function computeFracs(cpuTx: string, netTx: string, duration: number): { cpuFrac: number; netFrac: number } {
  const cpuAmount = BigInt(Math.max(0, Math.round((parseFloat(cpuTx) || 0) * duration)))
  const netAmount = BigInt(Math.max(0, Math.round((parseFloat(netTx) || 0) * duration)))
  return {
    cpuFrac: Number((CPU_FULL_FRAC * cpuAmount) / 1_000_000n),
    netFrac: Number((CPU_FULL_FRAC * netAmount) / 1_000_000n),
  }
}

export default function ResourcesScreen() {
  const { t } = useTranslation()
  const { activeAccount, activeSession, goBack, resources, navigate } = useWalletStore()
  const { selectedExplorer, setDiscoveryUrl } = useAppStore()

  // Same "did it actually go through?" confirmation TransferScreen/NftSendModal
  // show — PowerUp/Buy RAM/Sell RAM used to only leave a one-line success
  // string under the button, easy to miss above the fold once the button
  // itself moves out of view.
  const [successModal, setSuccessModal] = useState<{ label: string; txid: string } | null>(null)

  // PowerUp state
  const [cpuTx, setCpuTx] = useState('')
  const [netTx, setNetTx] = useState('')
  const [duration, setDuration] = useState(1)
  const [receiver, setReceiver] = useState(activeAccount?.name ?? '')
  const [puLoading, setPuLoading] = useState(false)
  const [puError, setPuError] = useState('')
  const [puSuccess, setPuSuccess] = useState('')

  // RAM state. Two inputs, one number: you can say "12 KB" or "4 EOS" and the
  // other side follows. `ramEdit` records which one you touched last, so the
  // conversion never fights the field under your cursor.
  const [ramKb, setRamKb] = useState('1')
  const [ramEos, setRamEos] = useState('')
  const [ramEdit, setRamEdit] = useState<'kb' | 'eos'>('kb')
  const [ramPrice, setRamPrice] = useState<RamPrice | null>(null)
  // RAM can be bought for somebody else — that is how you fund a fresh contract
  // account, which cannot pay for its own code.
  const [ramForOther, setRamForOther] = useState(false)
  const [ramReceiver, setRamReceiver] = useState('')
  const [ramReceiverMissing, setRamReceiverMissing] = useState(false)
  const [ramLoading, setRamLoading] = useState(false)
  const [ramError, setRamError] = useState('')
  const [ramSuccess, setRamSuccess] = useState('')

  // Sell RAM state — same two-way KB/EOS input pattern as buying, but only
  // ever against your own account: `sellram` credits the seller directly,
  // there is no "for someone else" concept on this action.
  const [sellRamKb, setSellRamKb] = useState('1')
  const [sellRamEos, setSellRamEos] = useState('')
  const [sellRamEdit, setSellRamEdit] = useState<'kb' | 'eos'>('kb')
  const [sellLoading, setSellLoading] = useState(false)
  const [sellError, setSellError] = useState('')
  const [sellSuccess, setSellSuccess] = useState('')

  // Live on-chain fee estimate (core-symbol smallest units), refetched as inputs change.
  const [estimatedFeeAtomic, setEstimatedFeeAtomic] = useState<number | null>(null)
  const [estimating, setEstimating] = useState(false)
  const estimatedCost = estimatedFeeAtomic != null ? estimatedFeeAtomic / 10_000 : 0

  // Live RAM price from eosio:rammarket. Refreshed on a slow timer — the price
  // moves, and a stale quote is what made the last purchase look wrong.
  useEffect(() => {
    let cancelled = false
    const load = () => {
      void fetchRamPrice().then(p => {
        if (!cancelled && p) setRamPrice(p)
      })
    }
    load()
    const timer = setInterval(load, 60_000)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [])

  // KB -> EOS, or EOS -> KB, whichever direction you are not typing in.
  useEffect(() => {
    if (!ramPrice) return
    if (ramEdit === 'kb') {
      const bytes = Math.round((parseFloat(ramKb) || 0) * 1024)
      setRamEos(bytes > 0 ? ramEosForBytes(bytes, ramPrice).toFixed(4) : '')
    } else {
      const eos = parseFloat(ramEos) || 0
      const bytes = ramBytesForEos(eos, ramPrice)
      setRamKb(bytes > 0 ? (bytes / 1024).toFixed(2) : '')
    }
  }, [ramKb, ramEos, ramEdit, ramPrice])

  const ramBytes = Math.round((parseFloat(ramKb) || 0) * 1024)
  const ramCost = ramPrice ? ramEosForBytes(ramBytes, ramPrice) : 0
  const ramTargetName = ramForOther ? ramReceiver.trim() : (activeAccount?.name ?? '')

  const sellRamBytes = Math.round((parseFloat(sellRamKb) || 0) * 1024)
  // Same rammarket quote used for buying — an estimate, not a guarantee: the
  // bancor-style price moves between this read and the actual sellram exec.
  const sellRamProceeds = ramPrice ? ramEosForBytes(sellRamBytes, ramPrice) : 0
  const ramAvailableToSell = resources ? Math.max(0, resources.ram.quota - resources.ram.used) : null

  useEffect(() => {
    const { cpuFrac, netFrac } = computeFracs(cpuTx, netTx, duration)
    if (cpuFrac <= 0 && netFrac <= 0) { setEstimatedFeeAtomic(0); return }
    let cancelled = false
    setEstimating(true)
    const timer = setTimeout(() => {
      void estimatePowerUpFeeFromFracs(cpuFrac, netFrac).then(fee => {
        if (!cancelled) { setEstimatedFeeAtomic(fee); setEstimating(false) }
      })
    }, 300)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [cpuTx, netTx, duration])

  const handlePowerUp = async () => {
    if (!activeSession || !activeAccount) return
    setPuError('')
    setPuSuccess('')
    setPuLoading(true)

    try {
      const { cpuFrac, netFrac } = computeFracs(cpuTx, netTx, duration)

      // Re-fetch the fee right before broadcasting — price drifts over time,
      // so we don't trust a possibly-stale value sitting in UI state.
      const freshFeeAtomic = await estimatePowerUpFeeFromFracs(cpuFrac, netFrac)
      if (freshFeeAtomic == null) {
        throw new Error('Could not fetch current PowerUp price, try again')
      }
      const maxPay = `${((freshFeeAtomic / 10_000) * MAX_PAYMENT_BUFFER).toFixed(4)} EOS`

      const txid = await powerUp(activeSession as Session, {
        payer: activeAccount.name,
        receiver: receiver || activeAccount.name,
        cpuFrac,
        netFrac,
        maxPayment: maxPay,
      })
      setPuSuccess(`✓ PowerUp done! TX: ${txid.slice(0, 16)}...`)
      setSuccessModal({ label: t('resources.powerUp'), txid })
    } catch (e) {
      setPuError(e instanceof Error ? e.message : 'PowerUp failed')
    } finally {
      setPuLoading(false)
    }
  }

  const handleBuyRam = async () => {
    if (!activeSession || !activeAccount) return
    setRamError('')
    setRamSuccess('')
    setRamLoading(true)

    try {
      const bytes = ramBytes
      if (bytes < 64) {
        setRamError(t('resources.ramTooSmall'))
        return
      }
      const receiverName = ramForOther ? ramReceiver.trim() : activeAccount.name

      if (ramForOther) {
        if (!/^[a-z1-5.]{1,13}$/.test(receiverName)) {
          setRamError(t('transfer.errors.invalidAccount'))
          return
        }
        // Cheaper to check than to burn a transaction on a typo.
        if (!(await accountExists(receiverName))) {
          setRamReceiverMissing(true)
          setRamError(t('resources.ramAccountMissing'))
          return
        }
      }

      const txid = await buyRam(activeSession as Session, {
        payer: activeAccount.name,
        receiver: receiverName,
        bytes,
      })
      setRamSuccess(`✓ ${(bytes / 1024).toFixed(2)} KB → ${receiverName}. TX: ${txid.slice(0, 16)}...`)
      setSuccessModal({ label: `${t('resources.buyRam')} (${(bytes / 1024).toFixed(2)} KB → ${receiverName})`, txid })
    } catch (e) {
      setRamError(e instanceof Error ? e.message : 'Buy RAM failed')
    } finally {
      setRamLoading(false)
    }
  }

  const handleSellRam = async () => {
    if (!activeSession || !activeAccount) return
    setSellError('')
    setSellSuccess('')
    setSellLoading(true)

    try {
      const bytes = sellRamBytes
      if (bytes < 64) {
        setSellError(t('resources.ramTooSmall'))
        return
      }
      if (ramAvailableToSell != null && bytes > ramAvailableToSell) {
        setSellError(t('resources.sellRamTooMuch'))
        return
      }

      const txid = await sellRam(activeSession as Session, activeAccount.name, bytes)
      setSellSuccess(`✓ ${(bytes / 1024).toFixed(2)} KB. TX: ${txid.slice(0, 16)}...`)
      setSuccessModal({ label: `${t('resources.sellRam')} (${(bytes / 1024).toFixed(2)} KB)`, txid })
    } catch (e) {
      setSellError(e instanceof Error ? e.message : 'Sell RAM failed')
    } finally {
      setSellLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-surface">
      {/* Header */}
      <div className="bg-surface-1 pt-safe px-4 py-3 border-b border-surface-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={goBack} className="text-gray-400 text-sm">← {t('common.back')}</button>
        <h2 className="text-white font-semibold flex-1 text-center">{t('resources.title')}</h2>
        <div className="w-16" />
      </div>

      <div className="scroll-area flex-1 px-4 py-4 space-y-4">
        {/* Fuel note */}
        <div className="bg-brand/10 border border-brand/30 rounded-xl px-3 py-2">
          <p className="text-xs text-brand-light">⚡ {t('resources.fuelAuto')}</p>
        </div>

        {/* PowerUp */}
        <div className="card space-y-3">
          <p className="text-sm font-semibold text-white">{t('resources.powerUp')}</p>

          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">{t('resources.cpuTransactions')}</label>
            <div className="flex gap-2 flex-wrap mb-1">
              {CPU_PRESETS.map(p => (
                <button key={p} onClick={() => setCpuTx(String(p))}
                  className={`px-2 py-1 rounded-lg text-xs ${cpuTx === String(p) ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'}`}>
                  {p}
                </button>
              ))}
            </div>
            <input type="number" value={cpuTx} onChange={e => setCpuTx(e.target.value)}
              placeholder="0" className="input-field text-sm py-2" />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">{t('resources.netTransactions')}</label>
            <div className="flex gap-2 flex-wrap mb-1">
              {NET_PRESETS.map(p => (
                <button key={p} onClick={() => setNetTx(String(p))}
                  className={`px-2 py-1 rounded-lg text-xs ${netTx === String(p) ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'}`}>
                  {p}
                </button>
              ))}
            </div>
            <input type="number" value={netTx} onChange={e => setNetTx(e.target.value)}
              placeholder="0" className="input-field text-sm py-2" />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">{t('resources.duration')}: {duration}</label>
            <input type="range" min="1" max="30" value={duration}
              onChange={e => setDuration(parseInt(e.target.value))}
              className="w-full accent-brand" />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">Receiver</label>
            <input value={receiver} onChange={e => setReceiver(e.target.value)}
              className="input-field text-sm py-2" placeholder={activeAccount?.name} />
          </div>

          <div className="flex justify-between text-xs text-gray-400 border-t border-surface-3 pt-2">
            <span>{t('resources.estimatedCost')}:</span>
            <span className="text-white font-medium">
              {estimating ? '…' : `~${estimatedCost.toFixed(4)} EOS`}
            </span>
          </div>

          {puError && <p className="text-danger text-xs">{puError}</p>}
          {puSuccess && <p className="text-success text-xs">{puSuccess}</p>}

          <button onClick={handlePowerUp} disabled={puLoading || !activeSession}
            className="btn-primary w-full text-sm py-2.5">
            {puLoading ? t('common.loading') : t('resources.powerUp')}
          </button>
        </div>

        {/* Buy RAM */}
        <div className="card space-y-3">
          <p className="text-sm font-semibold text-white">{t('resources.buyRam')}</p>

          {resources && (
            <div className="space-y-1.5">
              <div className="resource-bar">
                <div
                  className={`resource-bar-fill ${resources.ram.percentUsed > 90 ? 'bg-danger' : resources.ram.percentUsed > 70 ? 'bg-warning' : 'bg-success'}`}
                  style={{ width: `${Math.min(resources.ram.percentUsed, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-[11px] text-gray-500">
                <span>{fmtBytes(resources.ram.used)}</span>
                <span className="text-gray-600">/ {fmtBytes(resources.ram.quota)} ({resources.ram.percentUsed}%)</span>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">{t('resources.ramReceiver')}</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => { setRamForOther(false); setRamReceiverMissing(false); setRamError('') }}
                className={ramForOther ? 'btn-ghost flex-1 text-xs py-1.5' : 'btn-primary flex-1 text-xs py-1.5'}
              >
                {t('resources.ramForSelf')}
              </button>
              <button
                type="button"
                onClick={() => setRamForOther(true)}
                className={ramForOther ? 'btn-primary flex-1 text-xs py-1.5' : 'btn-ghost flex-1 text-xs py-1.5'}
              >
                {t('resources.ramOther')}
              </button>
            </div>
            {ramForOther && (
              <>
                <input
                  type="text"
                  value={ramReceiver}
                  onChange={e => {
                    setRamReceiver(e.target.value.toLowerCase().replace(/[^a-z1-5.]/g, ''))
                    setRamReceiverMissing(false)
                  }}
                  placeholder="properavatar"
                  maxLength={13}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="input-field text-sm py-2"
                />
                <p className="text-[11px]" style={{ color: ramReceiverMissing ? '#f87171' : '#6b7280' }}>
                  {ramReceiverMissing ? t('resources.ramAccountMissing') : t('resources.ramReceiverHint')}
                </p>
              </>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex gap-1.5 flex-wrap">
              {RAM_PRESETS_KB.map(kb => (
                <button
                  key={kb}
                  type="button"
                  onClick={() => { setRamEdit('kb'); setRamKb(String(kb)) }}
                  className={`px-2.5 py-1 rounded-lg text-xs ${
                    ramEdit === 'kb' && ramBytes === kb * 1024 ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'
                  }`}
                >
                  {kb} KB
                </button>
              ))}
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {RAM_PRESETS_EOS.map(eos => (
                <button
                  key={eos}
                  type="button"
                  onClick={() => { setRamEdit('eos'); setRamEos(String(eos)) }}
                  className={`px-2.5 py-1 rounded-lg text-xs ${
                    ramEdit === 'eos' && ramEos === String(eos) ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'
                  }`}
                >
                  {eos} EOS
                </button>
              ))}
            </div>

            {/* Both fields are live. Type in either one. */}
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <label className="text-[11px] text-gray-500">{t('resources.ramKilobytes')}</label>
                <div className="relative">
                  <input
                    type="text"
                    value={ramKb}
                    onChange={e => { setRamEdit('kb'); setRamKb(e.target.value.replace(/[^0-9.]/g, '')) }}
                    placeholder="0"
                    inputMode="decimal"
                    className="input-field text-sm py-2 pr-9"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-500">KB</span>
                </div>
              </div>

              <span className="pb-2.5 text-gray-600 text-sm">⇄</span>

              <div className="flex-1 space-y-1">
                <label className="text-[11px] text-gray-500">{t('resources.ramInEos')}</label>
                <div className="relative">
                  <input
                    type="text"
                    value={ramEos}
                    onChange={e => { setRamEdit('eos'); setRamEos(e.target.value.replace(/[^0-9.]/g, '')) }}
                    placeholder="0.0000"
                    inputMode="decimal"
                    className="input-field text-sm py-2 pr-11"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-500">EOS</span>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-gray-500">
              {ramPrice
                ? `${t('resources.ramPrice')}: ${ramPrice.eosPerKb.toFixed(4)} EOS / KB`
                : t('common.loading')}
            </p>
          </div>

          {/* Who is paying what, to whom — spelled out, because buying RAM for
              someone else is irreversible and easy to get wrong. */}
          {ramBytes > 0 && (
            <div className="rounded-xl border border-brand/30 bg-brand/10 px-3 py-2.5 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">{t('resources.ramSummaryPayer')}</span>
                <span className="text-white font-medium">{activeAccount?.name ?? '—'}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">{t('resources.ramSummaryReceiver')}</span>
                <span className={ramTargetName ? 'text-brand-light font-medium' : 'text-danger'}>
                  {ramTargetName || t('resources.ramSummaryNoReceiver')}
                </span>
              </div>
              <div className="flex justify-between text-xs border-t border-brand/20 pt-1.5">
                <span className="text-gray-400">{t('resources.ramSummaryGets')}</span>
                <span className="text-white font-medium">
                  {(ramBytes / 1024).toFixed(2)} KB
                  <span className="text-gray-500"> ({ramBytes.toLocaleString()} B)</span>
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">{t('resources.ramSummaryCosts')}</span>
                <span className="text-brand-light font-semibold">
                  {ramPrice ? `≈ ${ramCost.toFixed(4)} EOS` : '…'}
                </span>
              </div>
            </div>
          )}

          {ramError && <p className="text-danger text-xs">{ramError}</p>}
          {ramSuccess && <p className="text-success text-xs">{ramSuccess}</p>}

          <button onClick={handleBuyRam} disabled={ramLoading || !activeSession || ramBytes < 64 || !ramTargetName}
            className="btn-primary w-full text-sm py-2.5">
            {ramLoading ? t('common.loading') : t('resources.buyRam')}
          </button>
        </div>

        {/* Sell RAM — always your own account, chain-enforced (sellram credits the seller). */}
        <div className="card space-y-3">
          <p className="text-sm font-semibold text-white">{t('resources.sellRam')}</p>

          {resources && (
            <div className="space-y-1.5">
              <div className="resource-bar">
                <div
                  className={`resource-bar-fill ${resources.ram.percentUsed > 90 ? 'bg-danger' : resources.ram.percentUsed > 70 ? 'bg-warning' : 'bg-success'}`}
                  style={{ width: `${Math.min(resources.ram.percentUsed, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-[11px] text-gray-500">
                <span>{fmtBytes(resources.ram.used)}</span>
                <span className="text-gray-600">/ {fmtBytes(resources.ram.quota)} ({resources.ram.percentUsed}%)</span>
              </div>
              {ramAvailableToSell != null && (
                <p className="text-[11px] text-gray-500">
                  {t('resources.sellRamAvailable')}: {fmtBytes(ramAvailableToSell)}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-1 space-y-1">
              <label className="text-xs text-gray-400">{t('resources.ramKilobytes')}</label>
              <div className="relative">
                <input
                  value={sellRamKb}
                  onChange={e => {
                    setSellRamEdit('kb')
                    setSellRamKb(e.target.value)
                    const kb = parseFloat(e.target.value) || 0
                    setSellRamEos(ramPrice ? ramEosForBytes(Math.round(kb * 1024), ramPrice).toFixed(4) : '')
                  }}
                  placeholder="1"
                  inputMode="decimal"
                  className="input-field text-sm py-2 pr-11"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-500">KB</span>
              </div>
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-xs text-gray-400">≈</label>
              <div className="relative">
                <input
                  value={sellRamEos}
                  onChange={e => {
                    setSellRamEdit('eos')
                    setSellRamEos(e.target.value)
                    const eos = parseFloat(e.target.value) || 0
                    setSellRamKb(ramPrice ? (ramBytesForEos(eos, ramPrice) / 1024).toFixed(2) : '')
                  }}
                  placeholder="0.0000"
                  inputMode="decimal"
                  className="input-field text-sm py-2 pr-11"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-500">EOS</span>
              </div>
            </div>
          </div>

          {sellRamBytes > 0 && (
            <div className="rounded-xl border border-brand/30 bg-brand/10 px-3 py-2.5 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">{t('resources.sellRamGives')}</span>
                <span className="text-white font-medium">
                  {(sellRamBytes / 1024).toFixed(2)} KB
                  <span className="text-gray-500"> ({sellRamBytes.toLocaleString()} B)</span>
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">{t('resources.sellRamProceeds')}</span>
                <span className="text-brand-light font-semibold">
                  {ramPrice ? `≈ ${sellRamProceeds.toFixed(4)} EOS` : '…'}
                </span>
              </div>
            </div>
          )}

          {sellError && <p className="text-danger text-xs">{sellError}</p>}
          {sellSuccess && <p className="text-success text-xs">{sellSuccess}</p>}

          <button
            onClick={handleSellRam}
            disabled={sellLoading || !activeSession || sellRamBytes < 64}
            className="btn-primary w-full text-sm py-2.5"
          >
            {sellLoading ? t('common.loading') : t('resources.sellRam')}
          </button>
        </div>
      </div>

      {successModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label={t('common.close')}
            onClick={() => setSuccessModal(null)}
          />
          <div
            className="relative w-full max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col overflow-hidden mt-auto sm:mt-0 p-4 space-y-4"
            style={{ background: '#0c100c', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="text-center space-y-2 py-2">
              <div
                className="w-14 h-14 mx-auto rounded-full flex items-center justify-center"
                style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)' }}
              >
                <span className="text-2xl text-success">✓</span>
              </div>
              <p className="text-sm text-white">{successModal.label}</p>
            </div>
            <div className="card w-full space-y-2">
              <p className="text-xs text-gray-500">{t('transfer.txId')}</p>
              <p className="text-xs font-mono text-gray-300 break-all">{successModal.txid}</p>
              <button
                type="button"
                onClick={() => {
                  setDiscoveryUrl(getExplorerUrl(selectedExplorer, 'tx', successModal.txid))
                  setSuccessModal(null)
                  navigate('discovery')
                }}
                className="text-xs text-brand-light underline"
              >
                {t('transfer.viewOnExplorer')} →
              </button>
            </div>
            <button className="btn-primary w-full" onClick={() => setSuccessModal(null)}>
              {t('common.done')}
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  )
}
